// Rows reordered by hand: dragged onto another row of their list, or moved
// one place with Alt+↑/↓. What is saved is the list's ids in their new
// order; the node (/api/order/{list}) and central (/api/fleet/order) move
// only the ids sent, among the places they hold, so a part of a list (one
// encode's destinations, the nodes a filter shows) is sent alone.

import { nextTick, ref } from 'vue'

// moveTo: ids with id moved to index to (clamped); null when nothing moves
export function moveTo(ids: readonly string[], id: string, to: number): string[] | null {
    const from = ids.indexOf(id)
    to = Math.max(0, Math.min(ids.length - 1, to))
    if (from < 0 || from === to) return null
    const out = ids.filter((x) => x !== id)
    out.splice(to, 0, id)
    return out
}

// the drop marker: a line on the row's edge where the dragged one will land
// (below a row further down, above one further up), the dragged row dimmed
const line =
    "relative before:pointer-events-none before:absolute before:inset-x-0 before:z-10 before:h-0.5 before:bg-accent before:content-['']"
export const marker = { above: `${line} before:top-0`, below: `${line} before:bottom-0`, dragged: 'opacity-50' }

export interface Movable {
    draggable: 'true'
    class: string
    onDragstart(e: DragEvent): void
    onDragover(e: DragEvent): void
    onDragleave(e: DragEvent): void
    onDrop(e: DragEvent): void
    onDragend(): void
    onKeydown(e: KeyboardEvent): void
}

// useReorder: the attributes that make a row of a list movable, for
// v-bind. save gets the list's ids in their new order. A row dragged onto a
// row of another list (another encode's destination) does nothing and
// shows no marker.
export function useReorder(save: (ids: string[]) => Promise<void>) {
    const dragging = ref('')
    const over = ref('') // the row the dragged one is over
    const done = () => {
        dragging.value = ''
        over.value = ''
    }
    return (ids: () => string[], id: string, enabled: boolean): Partial<Movable> => {
        if (!enabled) return {}
        const from = ids().indexOf(dragging.value)
        const to = ids().indexOf(id)
        const where =
            id === dragging.value
                ? marker.dragged
                : over.value === id && from >= 0
                  ? to > from
                      ? marker.below
                      : marker.above
                  : ''
        return {
            draggable: 'true',
            class: where,
            onDragstart: (e: DragEvent) => {
                dragging.value = id
                if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
                e.stopPropagation() // a row inside a draggable card is not the card
            },
            onDragover: (e: DragEvent) => {
                if (!ids().includes(dragging.value)) return
                e.preventDefault()
                e.stopPropagation() // a row's marker, not its card's
                over.value = id
            },
            // left for somewhere not to drop (another list, outside): no
            // line; into one of its own children it is still over the row
            onDragleave: (e: DragEvent) => {
                const row = e.currentTarget as Element | null
                if (over.value === id && !row?.contains?.(e.relatedTarget as Node | null)) over.value = ''
            },
            onDrop: (e: DragEvent) => {
                if (!ids().includes(dragging.value)) return
                e.preventDefault()
                e.stopPropagation()
                const next = moveTo(ids(), dragging.value, ids().indexOf(id))
                done()
                if (next) void save(next)
            },
            onDragend: done,
            onKeydown: (e: KeyboardEvent) => {
                if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return
                e.preventDefault()
                e.stopPropagation()
                const next = moveTo(ids(), id, ids().indexOf(id) + (e.key === 'ArrowUp' ? -1 : 1))
                // the row moved in the page loses focus: given back, so a
                // second Alt+↑ moves it again
                const el = globalThis.document?.activeElement as HTMLElement | null | undefined
                if (next)
                    void save(next)
                        .then(() => nextTick())
                        .then(() => el?.focus())
            },
        }
    }
}
